import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { Check, ChevronsUpDown, X } from 'lucide-react';

export interface MultiSelectOption {
  /** Stable value sent to the API. */
  value: string;
  /** Human-readable label rendered in the dropdown. */
  label: string;
}

export interface MultiSelectProps {
  /** Currently-selected values. */
  value: string[];
  /** Setter. */
  onChange: (next: string[]) => void;
  /** Available options. */
  options: MultiSelectOption[];
  /** Placeholder shown in the trigger when nothing is selected. */
  placeholder?: string;
}

/**
 * Headless UI Listbox in `multiple` mode, styled to match the existing
 * `.input` look. The trigger shows a comma-separated list of selected
 * labels (truncated) and a "Clear" button when there's at least one
 * selection.
 */
export const MultiSelect = ({ value, onChange, options, placeholder = 'All' }: MultiSelectProps) => {
  const labelByValue = new Map(options.map((o) => [o.value, o.label]));
  const selectedLabels = value.map((v) => labelByValue.get(v) ?? v);
  const display = selectedLabels.length === 0 ? placeholder : selectedLabels.join(', ');

  return (
    <Listbox value={value} onChange={onChange} multiple>
      <div className="relative">
        <ListboxButton className="input flex items-center justify-between gap-2 text-left">
          <span className={`truncate flex-1 ${value.length === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
            {display}
          </span>
          {value.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onChange([]);
              }}
              className="text-gray-400 hover:text-gray-600 shrink-0"
              aria-label="Clear selection"
              title="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronsUpDown className="h-4 w-4 text-gray-400 shrink-0" />
        </ListboxButton>
        <ListboxOptions className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-gray-500">No options</div>
          ) : (
            options.map((option) => (
              <ListboxOption
                key={option.value}
                value={option.value}
                className={({ focus }) =>
                  `relative cursor-default select-none py-2 pl-9 pr-3 ${
                    focus ? 'bg-blue-600 text-white' : 'text-gray-900'
                  }`
                }
              >
                {({ selected, focus }) => (
                  <>
                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                      {option.label}
                    </span>
                    {selected && (
                      <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-2 ${
                          focus ? 'text-white' : 'text-blue-600'
                        }`}
                      >
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                  </>
                )}
              </ListboxOption>
            ))
          )}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};

export default MultiSelect;
